// Umwelt in den Objektmappen: Überblick über alle Anlagen und Fragen 1a) bis 6)
// des Umwelt-Fragebogens (`risikoUmweltStep/schema.ts`) — je Zeile, ob die
// Anlage vorhanden ist, was es ist und wie viel. Nicht vorhandene Anlagen
// stehen ausdrücklich mit „nein (nicht vorhanden)" da: Gerade die Lücke ist in
// der Besprechung die Aussage.

import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { charOf, contactLabel, eigentuemerVon, eur, type MappeRisk } from "./mappeDaten";

interface UmweltZeile {
  nr: string;
  label: string;
  /** Ja/Nein-Frage („ja" | „nein"). */
  vorhanden: string;
  /** Art bzw. Beschreibung. */
  was: string;
  /** Menge, Liter, Häufigkeit. */
  menge?: string;
  mengeEinheit?: string;
  baujahr?: string;
}

/** Schlüssel aus dem Umwelt-Fragebogen — dieselben, die als Merkmale gespeichert werden. */
export const UMWELT_ZEILEN: UmweltZeile[] = [
  {
    nr: "1a)",
    label: "WHG-Anlagen: Kleingebinde und Maschineninhalte",
    vorhanden: "whg_kb_vorhanden",
    was: "whg_kb_welche",
    menge: "whg_kb_gesamtmenge",
  },
  {
    nr: "1b)",
    label: "WHG-Anlagen: Tanks",
    vorhanden: "whg_tanks_vorhanden",
    was: "whg_tanks_art",
    menge: "whg_tanks_liter",
    mengeEinheit: "l",
    baujahr: "whg_tanks_baujahr",
  },
  {
    nr: "1c)",
    label: "WHG-Anlagen: Trafos mit Kühlöl (PCB-frei)",
    vorhanden: "whg_trafos_vorhanden",
    was: "whg_trafos_art",
    menge: "whg_trafos_liter",
    mengeEinheit: "l",
    baujahr: "whg_trafos_baujahr",
  },
  {
    nr: "2a)",
    label: "UmweltHG-Anlagen (Anhang 1)",
    vorhanden: "umwelthg_2a_vorhanden",
    was: "umwelthg_2a_art",
    menge: "umwelthg_2a_menge",
    baujahr: "umwelthg_2a_baujahr",
  },
  {
    nr: "2b)",
    label: "UmweltHG-Anlage / Pflichtversicherung (Anhang 2)",
    vorhanden: "umwelthg_2b_vorhanden",
    was: "umwelthg_2b_art",
    menge: "umwelthg_2b_menge",
    baujahr: "umwelthg_2b_baujahr",
  },
  {
    nr: "3)",
    label: "Sonstige deklarierungspflichtige Anlagen",
    vorhanden: "sonstige_3_vorhanden",
    was: "sonstige_3_art",
    menge: "sonstige_3_menge",
    baujahr: "sonstige_3_baujahr",
  },
  {
    nr: "4)",
    label: "Abwasseranlagen & Einwirkungsrisiko",
    vorhanden: "abwasser_4_vorhanden",
    was: "abwasser_4_art",
    menge: "abwasser_4_menge",
    baujahr: "abwasser_4_baujahr",
  },
  {
    nr: "5)",
    label: "Tätigkeiten im Zusammenhang mit Umwelt-Anlagen",
    vorhanden: "umw_05",
    was: "umw_05_was",
  },
  {
    nr: "6)",
    label: "Geothermiebohrung auf eigenen Grundstücken",
    vorhanden: "umw_06",
    was: "umw_06_was",
    menge: "umw_06_wie_oft",
  },
];

/** Auswahlwerte der Gesamtmenge (Kleingebinde) lesbar machen. */
const MENGE_LABEL: Record<string, string> = {
  bis_1000l: "bis 1.000 l",
  bis_2000l: "bis 2.000 l",
  bis_3000l: "bis 3.000 l",
  ueber_3000l: "über 3.000 l",
};

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", rowGap: "10px" },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  table: { borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" },
  caption: {
    textAlign: "left",
    fontSize: "0.8rem",
    fontWeight: 700,
    color: BRAND.ink,
    paddingTop: "8px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  th: {
    textAlign: "left",
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderBottom("1px", "solid", BRAND.border),
  },
  td: {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    verticalAlign: "top",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  nr: { color: BRAND.muted, whiteSpace: "nowrap", width: "36px" },
  ja: { color: "#a32b2b", fontWeight: 700 },
  nein: { color: BRAND.muted },
  hinweis: { fontSize: "0.78rem", color: BRAND.muted, fontStyle: "italic" },
});

/** Wert eines Merkmals oder null — für eine fehlende Analyse immer null. */
type Lesen = (key: string) => string | null;

function Tabelle({ titel, lesen }: { titel?: string; lesen: Lesen }) {
  const styles = useStyles();
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        {titel && <caption className={styles.caption}>{titel}</caption>}
        <thead>
          <tr>
            <th className={styles.th}>Nr.</th>
            <th className={styles.th}>Anlage / Frage</th>
            <th className={styles.th}>Vorhanden</th>
            <th className={styles.th}>Was</th>
            <th className={styles.th}>Menge / Wert</th>
          </tr>
        </thead>
        <tbody>
          {UMWELT_ZEILEN.map((z) => {
            const ja = (lesen(z.vorhanden) ?? "").toLowerCase() === "ja";
            const mengeRoh = z.menge ? lesen(z.menge) : null;
            const menge = mengeRoh
              ? `${MENGE_LABEL[mengeRoh] ?? mengeRoh}${z.mengeEinheit ? ` ${z.mengeEinheit}` : ""}`
              : null;
            const baujahr = z.baujahr ? lesen(z.baujahr) : null;
            const umfang = [menge, baujahr ? `Baujahr ${baujahr}` : null].filter(Boolean).join(" · ");
            return (
              <tr key={z.nr}>
                <td className={mergeClasses(styles.td, styles.nr)}>{z.nr}</td>
                <td className={styles.td}>{z.label}</td>
                <td className={mergeClasses(styles.td, ja ? styles.ja : styles.nein)}>
                  {ja ? "ja" : "nein (nicht vorhanden)"}
                </td>
                <td className={styles.td}>{ja ? (lesen(z.was) ?? "—") : "–"}</td>
                <td className={styles.td}>{ja ? umfang || "—" : "–"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function UmweltUebersicht({ risks }: { risks: MappeRisk[] }) {
  const styles = useStyles();
  if (risks.length === 0)
    return (
      <div className={styles.wrap}>
        <div className={styles.hinweis}>
          Noch keine Umwelt-Analyse erfasst — über „+" anlegen.
        </div>
        <Tabelle lesen={() => null} />
      </div>
    );
  return (
    <div className={styles.wrap}>
      {risks.map((r) => {
        const wert = Number(r.versicherungssumme);
        const titel = [
          contactLabel(eigentuemerVon(r)),
          Number.isFinite(wert) && wert > 0 ? `Versicherungssumme ${eur(wert)}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <Tabelle
            key={r.id}
            titel={risks.length > 1 || titel.includes("·") ? titel : undefined}
            lesen={(key) => charOf(r, key)}
          />
        );
      })}
    </div>
  );
}
